export default class BCMSQuery {

  // CONSTRUCTOR :: [OBJECT] -> bcmsquery
  constructor(model) {
    this._model = model ?? {}
  }

  // :: (* -> *) -> [*]
  // Applies function to data of model:
  map(fn) {
    return this._model.map(fn);
  }

  // :: (* -> BOOLEAN) -> [*]
  // Applies predicate to data of model:
  filter(fn) {
    return this._model.filter(fn);
  }

  // ::  (a, * -> a), a -> a
  // Applies function and acculmator to data of model:
  reduce(fn, acc) {
    return this._model.reduce(fn, acc);
  }

  // :: (model:OBJECT -> *) -> *
  // Applies function to model in context of this instance of BCMSQuery:
  ap(fn) {
    return fn.apply(this, this._model);
  }

  // :: STRING, *, VOID|(OBJECT, *, OBJECT -> BOOLEAN) -> [OBJECT]
  // Returns data from model that has a property equal to the given value using an optional predicate:
  getBy(prop, val, pred) {
    return this.reduce((result, data) => {
      let args = [data[prop], data, this._model]
      return BCMSQuery.evalPredicate(pred, args, (valOfProp, data, model) => {
        return valOfProp === val
          ? result.concat(data)
          : result;
      }, () => result);
    }, []);
  }

  // :: STRING, (*, OBJECT, OBJECT -> BOOLEAN) -> [*]
  // Returns value of property for data of model using an optional predicate:
  getOnly(prop, pred) {
    return this.reduce((result, data) => {
      let args = [data[prop], data, this._model];
      return BCMSQuery.evalPredicate(pred, args, value => {
          return result.concat(value);
      }, () => result);
    }, []);
  }

  // :: STRING, VOID|(OBJECT, *, OBJECT -> BOOLEAN) -> OBJECT
  // Returns an object that binds the value of the given property to an object that value belongs to using an optional predicate:
  // NOTE: If a value is the same for more than one object, the object that value is bound to maybe overwritten:
  mapTo(prop, pred) {
    return this.reduce((result, data) => {
      let args = [data, data[prop], this._model];
      return BCMSQuery.evalPredicate(pred, args, (data, val, model) => {
        result[val] = data;
        return result;
      }, () => result);
    }, {});
  }

  // :: STRING, (OBJECT, *, OBJECT -> BOOLEAN) -> OBJECT
  // Groups data by value of given prop using an optional predicate:
  groupBy(prop, pred) {
    return this.reduce((result, data) => {
      let args = [data, data[prop], this._model];
      return BCMSQuery.evalPredicate(pred, args, (data, val, model) => {
        result[val] = result[val] === undefined
          ? [data]
          : result[val].concat(data);
        return result;
      }, () => result);
    }, {});
  }

  /**
   *
   *  Static Methods
   *
   */

   // Static Factory Method :: OBJECT -> bcmsquery
   static init(model) {
     return new BCMSQuery(model);
   }

   // :: (* -> BOOL), [*], ([*] -> *, [*] -> *) -> *
   // Applies args to predicate - and if TRUE, applies args to "onTrue" function, otherwise applies args to "onFalse" function
   // NOTE: If "pred" is not a function, then args are applied to "onTrue"
   static evalPredicate(pred, args, onTrue, onFalse) {
     let check = typeof(pred) === "function"
      ? () => pred.apply(null, args)
      : () => true;
     return check() === true
      ? onTrue.apply(null, args)
      : onFalse.apply(null, args);
   }

   // :: OBJECT, OBJECT, (OBJECT, OBJECT -> *) -> [*]
   // Applies a function to both objects that share the same key from the given objects:
   static matchKeys(mapA, mapB, fn) {
     let result = Object.entries(mapA).reduce((result, [key, value]) => {
       // This check ensures matched key is only added once:
       if (result[key] === undefined) {
         let valueB = mapB[key];
         result[key] = fn([value, valueB]);
       }
       return result;
     }, {});
     return Object.values(result);
   }

   // :: {model:[OBJECT], prop:STRING]}, {model:[OBJECT], prop:STRING} -> [OBJECT]
   // Replaces the value of property A in model A with the value of property B in model B if property A's value exists in model B:
   // NOTE: A copy of objects between both models is returned in the result:
   static resolve({model:modelA, prop:propA}, {model:modelB, prop:propB}) {
     let dataByProp = BCMSQuery.init(modelB).mapTo(propB);
     return modelA.map(data=>{
       let dataCopy = BCMSQuery.shallowCopy(data);
       let valueA = data[propA];
       let valueB = dataByProp[valueA];
       if (valueB !== undefined) {
         dataCopy[bindTo] = BCMSQuery.shallowCopy(valueB);
       }
       return dataCopy;
     })
   }

   // :: STRING, [a], [b], OBJECT|ARRAY, [propA, propB], BOOLEAN
   // Binds new property to data of model from another model where the value of propA from a is equal to the value of propB from b
   // NOTE: Result of augment is a copy of objects from the given models:
   // NOTE: Value bound to new property can either by a single OBJECT or an ARRAY of values
   // NOTE: By default "setEmpty" is FALSE, so the only values that are returned have matched properties between the given models
   // NOTE: If "setEmpty" is TRUE - then all values are returned regardless if properties are matched.
   //       Then if a property isn't matched, UNDEFINED is bound for "as" OBJECT or an emptry array is bound for "as" ARRAY.
   static augment({add, to, from, as, where}, setEmpty) {
     const [propA, propB] = where;
     const setIfEmpty = setEmpty ?? false;
     let dataByProp = as === "ARRAY"
      ? BCMSQuery.init(from).groupBy(propB)
      : BCMSQuery.init(from).mapTo(propB);
     return to.reduce((result,data)=>{
       let dataCopy = BCMSQuery.shallowCopy(data);
       let valueA = data[propA];
       if (valueA === undefined) {
         throw new Error(`"${propA}" is a property that doesn't exist for data in the "to" model!`);
       }
       let valueB = dataByProp[valueA];
       if (valueB !== undefined) {
         dataCopy[add] = BCMSQuery.shallowCopy(valueB);
         result.push(dataCopy)
       } else {
         if (setIfEmpty) {
           dataCopy[add] = as === "ARRAY" ? [] : undefined;
           result.push(dataCopy)
         }
       }
       return result;
     }, [])
   }

   // :: OBJECT -> OBJECT
   // Creates a shallow copy of the given object:
   static shallowCopy(obj) {
     if (Array.isArray(obj)) {
       return obj.map(BCMSQuery.shallowCopy)
     }
     return Object.assign(obj, {});
   }


}
